import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { Modal } from '@/shared/ui/Modal'
import { Pagination } from '@/shared/ui/Pagination'
import { Empty } from '@/shared/ui/Panel'
import { Segmented } from '@/shared/ui/Segmented'
import { Strip } from '@/shared/ui/Strip'
import { Switch } from '@/shared/ui/Switch'
import { ChevronRightIcon, EditIcon, PlusIcon, SearchIcon, TrashIcon, UserIcon } from '@/shared/ui/icons'
import { Section } from '@/shared/ui/Section'
import { fiat as fiatFormat } from '@/shared/lib/format'
import type { AdminAccountResponse, FiatCurrency } from '@/shared/api/types'
import { explain } from '@/shared/lib/errors'
import { useAuth, useStores } from '@/stores/RootStore'
import page from './Page.module.css'
import styles from './Admin.module.css'
import { useMeta } from '@/shared/lib/hooks/useMeta'

const ROLES = [
  { value: 'user' as const, label: 'Пользователь' },
  { value: 'admin' as const, label: 'Администратор' },
]

/**
 * Аккаунты мерчантов.
 *
 * Баланс в списке считает бекенд теми же курсами, что и кабинет
 * мерчанта: своя арифметика на клиенте завела бы вторую правду
 * о деньгах, и при разборе жалобы админ с мерчантом смотрели бы
 * на разные числа.
 */
export const AdminAccountsPage = observer(function AdminAccountsPage() {
  useMeta('Аккаунты')
  const { admin: store } = useStores()

  useEffect(() => { void store.accounts.load() }, [store])

  const items = store.accounts.value?.items ?? []

  const fiat = store.accounts.value?.fiat ?? 'usd'

  return (
    <div className={page.wide}>
      <Section
        title="Аккаунты"
        actions={
          <Button variant="primary" onClick={() => store.openCreate()}>
            <PlusIcon size={24} /> Создать
          </Button>
        }
      >
        {/* Поиска в макете нет, но список аккаунтов растёт: без него
            нужного мерчанта ищут листанием страниц. */}
        <Field placeholder="Почта аккаунта" value={store.search}
               icon={<SearchIcon size={24} />} aria-label="Поиск по почте"
               onChange={(e) => store.setSearch(e.target.value)} />

        {store.accounts.error && !store.accounts.loaded ? (
          <Strip tone="bad"
                 aside={<Button variant="secondary" onClick={() => void store.accounts.load()}>Ещё раз</Button>}>
            {explain(store.accounts.error)}
          </Strip>
        ) : null}

        {items.length === 0 && store.accounts.loaded ? (
          <Empty title="Аккаунтов не найдено" />
        ) : (
          <>
            <div className={styles.table} role="table">
              <div className={styles.thead} role="row">
                <span role="columnheader">Аккаунт</span>
                <span role="columnheader">Комиссия</span>
                <span role="columnheader">Баланс</span>
                <span role="columnheader">Статус</span>
                <span role="columnheader">Действия</span>
              </div>
              <div className={styles.rows}>
                {items.map((account) => (
                  <AccountRow key={account.id} account={account} fiat={fiat} />
                ))}
              </div>
            </div>

            <Pagination total={store.accounts.value?.total ?? 0} limit={store.limit}
                        offset={store.offset}
                        onChange={(offset) => store.goToOffset(offset)} />
          </>
        )}
      </Section>

      <AccountDialogs />
    </div>
  )
})

const AccountRow = observer(function AccountRow(
  { account, fiat }: { account: AdminAccountResponse; fiat: FiatCurrency },
) {
  const { admin: store } = useStores()
  const auth = useAuth()
  const navigate = useNavigate()
  const self = auth.user?.id === account.id

  return (
    <div className={styles.row} role="row">
      <span className={styles.who} role="cell">
        <span className={styles.avatar} aria-hidden="true"><UserIcon size={24} /></span>
        <span className={styles.whoText}>
          <span className={styles.email}>{account.email}</span>
          <span className={styles.role}>
            {account.role === 'admin' ? 'Администратор' : 'Пользователь'}
            {self ? ' · это вы' : ''}
          </span>
        </span>
      </span>

      {/* data-label — подпись значения на телефоне, где шапки таблицы нет. */}
      <span role="cell" className={styles.num} data-label="Комиссия">
        {account.commission_percent}%
      </span>

      {/* Прочерк, а не ноль, когда баланс не посчитан: ноль читается
          как «у мерчанта пусто», и по нему принимают решения. */}
      <span role="cell" className={styles.num} data-label="Баланс">
        {account.balance_fiat == null ? '—' : fiatFormat(account.balance_fiat, fiat)}
      </span>

      <span role="cell" data-label="Активен">
        <Switch
          checked={account.enabled}
          disabled={store.busy || self}
          ariaLabel={account.enabled
            ? `Заблокировать ${account.email}`
            : `Включить ${account.email}`}
          onChange={(next) => void store.setEnabled(account, next)}
        />
      </span>

      {/* На широких экранах у двух кнопок подписи, ниже 1280 — только
          значки: в макете колонка действий там 160px, и три подписи
          в неё не помещаются. Подпись остаётся в aria-label. */}
      <span className={styles.acts} role="cell">
        {/* Себя удалить нельзя: администратор, удаливший собственный
            аккаунт, закрывает админку для всех, если он был один. */}
        <Button variant="icon" tone="bad" disabled={self}
                aria-label={`Удалить ${account.email}`}
                onClick={() => store.openRemove(account)}>
          <TrashIcon size={24} />
        </Button>
        <Button variant="secondary" aria-label={`Изменить ${account.email}`}
                onClick={() => store.openEdit(account)}>
          <span className={styles.btnLabel}>Изменить</span>
          <EditIcon size={24} />
        </Button>
        <Button variant="primary" aria-label={`Открыть ${account.email}`}
                onClick={() => navigate(`/app/admin/accounts/${account.id}`)}>
          <span className={styles.btnLabel}>Открыть</span>
          <ChevronRightIcon size={24} />
        </Button>
      </span>
    </div>
  )
})

/**
 * Диалоги аккаунта (создать, изменить, удалить, сбросить 2FA).
 *
 * Экспортируются: кнопка «Изменить» есть и в карточке мерчанта, а состояние
 * диалога живёт в общем сторе. Без диалогов на той странице нажатие
 * молча ставило флаг, и модалка всплывала только при возврате к списку.
 */
export const AccountDialogs = observer(function AccountDialogs() {
  const { admin: store } = useStores()
  const dialog = store.dialog
  if (dialog.kind === 'none') return null

  if (dialog.kind === 'remove') {
    return (
      <Modal title="Удалить аккаунт?"
             note={`Вы уверены, что хотите удалить аккаунт «${dialog.account.email}»?`}
             onClose={() => store.close()}
             actions={
               <>
                 <Button variant="secondary" onClick={() => store.close()}>Не удалять</Button>
                 <Button variant="primary" busy={store.busy}
                         onClick={() => void store.remove()}>Да, удалить</Button>
               </>
             } />
    )
  }

  if (dialog.kind === 'reset2fa') {
    return (
      <Modal title="Сбросить 2FA?"
             note={`Вы уверены, что хотите сбросить двухфакторную аутентификацию для пользователя “${dialog.account.email}”?`}
             onClose={() => store.close()}
             actions={
               <>
                 <Button variant="secondary" onClick={() => store.close()}>Нет, оставить</Button>
                 <Button variant="primary" busy={store.busy}
                         onClick={() => void store.resetTwoFactor()}>Да, сбросить</Button>
               </>
             } />
    )
  }

  const creating = dialog.kind === 'create'
  const draft = store.draft

  return (
    <Modal title={creating ? 'Создать аккаунт' : draft.email}
           onClose={() => store.close()}
           actions={
             <>
               <Button variant="secondary" onClick={() => store.close()}>Отмена</Button>
               <Button variant="primary" busy={store.busy}
                       onClick={() => void store.submit()}>
                 {creating ? 'Создать' : 'Сохранить'}
               </Button>
             </>
           }>
      <Field label="Почта" type="email" placeholder="Укажите почту"
             value={draft.email} autoComplete="off"
             onChange={(e) => store.patch({ email: e.target.value })}
             error={store.errors.email ?? null} />

      <Field label={creating ? 'Пароль' : 'Новый пароль'} type="password"
             placeholder="••••••••••"
             value={draft.password} autoComplete="new-password"
             onChange={(e) => store.patch({ password: e.target.value })}
             error={store.errors.password ?? null} />

      <div className={styles.roleRow}>
        <span className={styles.roleLabel}>Роль</span>
        <Segmented ariaLabel="Роль" options={ROLES} value={draft.role}
                   onChange={(role) => store.patch({ role })} />
      </div>

      <Field label="Комиссия сервиса" numeric inputMode="decimal" param="%"
             placeholder="Укажите комиссию"
             value={draft.commission}
             onChange={(e) => store.patch({ commission: e.target.value })}
             error={store.errors.commission ?? null} />

      <Switch checked={draft.enabled} label="Аккаунт активен"
              onChange={(enabled) => store.patch({ enabled })} />

      <Switch checked={draft.paymentLinks} label="Платёжные ссылки"
              onChange={(paymentLinks) => store.patch({ paymentLinks })} />

      <Switch checked={draft.clientPaysFee} label="Комиссию сети платит покупатель"
              onChange={(clientPaysFee) => store.patch({ clientPaysFee })} />

      {creating ? null : (
        <Field label="Telegram для входа" numeric inputMode="numeric"
               placeholder="123456789"
               value={draft.telegram} autoComplete="off"
               onChange={(e) => store.patch({ telegram: e.target.value })}
               error={store.errors.telegram ?? null} />
      )}
    </Modal>
  )
})
